import type { ReactNode } from 'react';
import { isValidElement } from 'react';

const SLUG_DASH_RE = /[^a-z0-9]+/g;

const MONTHS: Record<string, number> = {
    january: 0,
    february: 1,
    march: 2,
    april: 3,
    may: 4,
    june: 5,
    july: 6,
    august: 7,
    september: 8,
    october: 9,
    november: 10,
    december: 11,
};

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const getOrdinalSuffix = (day: number): string => {
    const dayInHundreds = day % 100;
    if (dayInHundreds >= 11 && dayInHundreds <= 13) {
        return 'th';
    }

    const dayInTens = day % 10;
    if (dayInTens === 1) return 'st';
    if (dayInTens === 2) return 'nd';
    if (dayInTens === 3) return 'rd';
    return 'th';
};

/** Parse blog display dates like "May 15th 2026" for sorting. */
export const parseBlogDate = (date: string): number => {
    const match = /(\w+)\s+(\d+)(?:st|nd|rd|th)?\s+(\d{4})/i.exec(date);
    if (!match) {
        return 0;
    }

    const [, monthName, day, year] = match;
    const month = MONTHS[monthName.toLowerCase()];
    if (month === undefined) {
        return 0;
    }

    return new Date(Number(year), month, Number(day)).getTime();
};

/** Format ISO date string (e.g., "2026-06-16") to blog display format (e.g., "June 16th 2026"). */
export const formatBlogDate = (date: string): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!match) {
        return date; // Return as-is if not ISO format
    }

    const [, year, monthStr, dayStr] = match;
    const monthNum = Number(monthStr) - 1;
    const day = Number(dayStr);
    if (!Number.isInteger(monthNum) || monthNum < 0 || monthNum >= MONTH_NAMES.length || !Number.isInteger(day) || day < 1 || day > 31) {
        return date;
    }

    const suffix = getOrdinalSuffix(day);
    return `${MONTH_NAMES[monthNum]} ${day}${suffix} ${year}`;
};

export const sortBlogByDateDesc = <T extends { date: string }>(posts: T[]): T[] =>
    [...posts].sort((a, b) => parseBlogDate(b.date) - parseBlogDate(a.date));

export const generateSlug = (value: string): string =>
    value.toLowerCase().trim().replaceAll("'", '').replaceAll(SLUG_DASH_RE, '-').replace(/^-+/, '').replace(/-+$/, '');

export const extractTextFromJSX = (node: ReactNode): string => {
    if (node === null || node === undefined || typeof node === 'boolean') {
        return '';
    }
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(extractTextFromJSX).join(' ');
    }
    if (isValidElement(node)) {
        const props = (node.props ?? {}) as { children?: ReactNode };
        return extractTextFromJSX(props.children);
    }
    if (typeof node === 'object' && node !== null && 'children' in (node as unknown as Record<string, unknown>)) {
        return extractTextFromJSX((node as unknown as { children?: ReactNode }).children);
    }
    return '';
};

export const getReadingTime = (
    content: ReactNode,
    t: (key: string, options?: Record<string, unknown>) => string,
): string => {
    const wordsPerMinute = 200;
    const textContent = extractTextFromJSX(content);
    const words = textContent.split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(words / wordsPerMinute));
    return t('{{minutes}} min read', { minutes });
};

interface TagMapping {
    keywords: string[];
    minCount: number;
    tag: string;
}

const TAG_MAPPINGS: TagMapping[] = [
    { keywords: ['phishing', 'phish'], minCount: 3, tag: 'Phishing Detection' },
    {
        keywords: ['url scanner', 'link scanner', 'url analysis', 'url threat', 'malicious link', 'link checking'],
        minCount: 2,
        tag: 'URL Scanning',
    },
    {
        keywords: ['multiscanning', 'multi-scanning', 'multi scanning', 'multiple engines', 'multiple antivirus'],
        minCount: 2,
        tag: 'Multiscanning',
    },
    {
        keywords: ['cloud storage', 'cloud files', 'cloud-based', 'saas security', 'cloud environment'],
        minCount: 2,
        tag: 'Cloud Security',
    },
    { keywords: ['content disarm', 'cdr', 'deep cdr', 'file sanitization'], minCount: 2, tag: 'Deep CDR' },
    {
        keywords: ['data loss prevention', 'dlp', 'sensitive data', 'data leaks'],
        minCount: 2,
        tag: 'Data Loss Prevention',
    },
    {
        keywords: ['sandbox', 'sandboxing', 'adaptive sandbox', 'behavioral analysis'],
        minCount: 2,
        tag: 'Sandbox Analysis',
    },
    { keywords: ['zero trust', 'zero-trust'], minCount: 2, tag: 'Zero Trust' },
    {
        keywords: ['community intelligence', 'community-driven', 'collaborative', 'threat intelligence sharing'],
        minCount: 2,
        tag: 'Community',
    },
    { keywords: ['ransomware', 'credential theft', 'cyberattack'], minCount: 2, tag: 'Threat Analysis' },
    { keywords: ['sbom', 'software bill of materials', 'supply chain security'], minCount: 2, tag: 'SBOM' },
    {
        keywords: ['file security', 'file-borne threats', 'file scanning', 'document security'],
        minCount: 3,
        tag: 'File Security',
    },
    { keywords: ['malware detection', 'virus detection', 'threat detection'], minCount: 3, tag: 'Malware Detection' },
    { keywords: ['best practices', 'how to protect', 'security strategy'], minCount: 2, tag: 'Best Practices' },
];

export const generateTags = (content: ReactNode, title: string): string[] => {
    const text = `${title} ${extractTextFromJSX(content)}`.toLowerCase();
    const tagScores: Record<string, number> = {};

    for (const mapping of TAG_MAPPINGS) {
        let count = 0;
        for (const keyword of mapping.keywords) {
            const escapedKeyword = keyword.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // eslint-disable-next-line security/detect-non-literal-regexp
            const regex = new RegExp(escapedKeyword, 'gi');
            const matches = text.match(regex);
            if (matches) {
                count += matches.length;
            }
        }
        if (count >= mapping.minCount) {
            tagScores[mapping.tag] = count;
        }
    }

    const sortedTags = Object.keys(tagScores).sort((a, b) => tagScores[b] - tagScores[a]);
    const finalTags = ['MetaDefender'];
    sortedTags.slice(0, 5).forEach((tag) => {
        if (!finalTags.includes(tag)) {
            finalTags.push(tag);
        }
    });
    return finalTags;
};
